import { ShopOutlined } from '@ant-design/icons';
import { Select, Tooltip } from 'antd';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '../../features/auth/session';

/**
 * Switches the active branch, which every branch-scoped query then filters by.
 *
 * Clears the query cache on change. Without that, the customer list would keep
 * showing the previous branch's rows from cache while the header claims a
 * different branch — a mistake that looks like a data leak to anyone auditing it.
 *
 * Hidden when a user has only one branch, which is every NKS user today.
 */
export function BranchSwitcher(): ReactNode {
  const branches = useSession((s) => s.branches);
  const activeBranchUid = useSession((s) => s.activeBranchUid);
  const setBranch = useSession((s) => s.setBranch);
  const queryClient = useQueryClient();

  if (branches.length <= 1) {
    const only = branches[0];
    return only === undefined ? null : (
      <Tooltip title={only.name}>
        <span style={{ color: 'var(--ink-muted)', fontSize: 13 }}>
          <ShopOutlined style={{ marginRight: 6 }} />
          {only.code}
        </span>
      </Tooltip>
    );
  }

  return (
    <Select<string>
      value={activeBranchUid ?? undefined}
      onChange={(uid) => {
        setBranch(uid);
        queryClient.clear();
      }}
      options={branches.map((b) => ({ value: b.uid, label: `${b.code} — ${b.name}` }))}
      // Fills whatever the header wrapper allows rather than demanding 180px.
      // A hard minimum here is what forced the overflow onto the theme control
      // at 390px, because a Select that cannot shrink makes something else move.
      style={{ width: '100%', minWidth: 0 }}
      aria-label="Active branch"
    />
  );
}
